/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { LazyQueryExecFunction } from "@apollo/client";
import { createColumnHelper } from "@tanstack/react-table";
import { FlexContainer, Text, theme } from "@nordcloud/gnui";
import {
  Exact,
  GetEventViewQuery,
  ResourceExecutionErrorCodeCount,
} from "~/generated/graphql";
import { ExpandIcon } from "~/components";
import { StyledSVGIcon } from "~/components/Styled/StyledSVGIcon";
import { MediumIconColumn, SmallIconColumn } from "~/components/Tables/styles";
import { ResourceStateCounts } from "~/views/events/hooks/useEventActionResourceCounts";
import { getLatestAttemptSummaryId } from "../helpers";
import { EventEntity } from "../types";
import { EventActionStatus } from "./EvenActionStatus";
import { EventActionBatchStatus } from "./EventActionBatchStatus";
import type { EventActionDescription } from "./EventItem.utils";
import { getCountsFromResourceStates } from "./utils/getCountsFromResourceStates";

const entityRow = createColumnHelper<EventEntity>();

export function eventEntitiesColumns({
  refreshEvent,
  getCountsForAction,
  getActionErrorCodeCounts,
  getActionDescription,
  isEventFinished,
}: {
  refreshEvent: LazyQueryExecFunction<
    GetEventViewQuery,
    Exact<{
      id: string;
    }>
  >;
  getCountsForAction: (eventActionId: string) => ResourceStateCounts;
  getActionErrorCodeCounts: (
    eventEntity: Pick<EventEntity, "id" | "attempts">
  ) => ResourceExecutionErrorCodeCount[];
  getActionDescription: (
    eventEntity: EventEntity
  ) => EventActionDescription | undefined;
  isEventFinished: boolean;
}) {
  const baseColumns = [
    entityRow.accessor(() => null, {
      header: "",
      id: "dummy",
      meta: SmallIconColumn,
      cell: ({ row }) => {
        return row.getCanExpand() ? (
          <ExpandIcon
            name="chevronDown"
            title="chevronDown"
            isExpanded={row.getIsExpanded()}
            onClick={row.getToggleExpandedHandler()}
          />
        ) : null;
      },
    }),
    entityRow.accessor(() => null, {
      header: "",
      id: "state",
      cell: ({ row }) => {
        return (
          <div
            style={{
              paddingLeft: `${row.depth * 4}rem`,
            }}
          >
            {row.getCanExpand() ? (
              <EventActionBatchStatus
                eventActionBatchId={row.original.id}
                eventActionBatchStatus={row.original?.status}
                batchActions={row.original?.actions}
                onEventActionBatchStatusChanged={refreshEvent}
              />
            ) : (
              <EventActionStatus
                eventActionId={getLatestAttemptSummaryId(row.original)}
                eventActionStatus={row.original?.globalState?.status}
                errorCodeCounts={getActionErrorCodeCounts(row.original)}
                isEventFinished={isEventFinished}
                onEventActionStatusChanged={refreshEvent}
              />
            )}
          </div>
        );
      },
      meta: MediumIconColumn,
    }),
    entityRow.accessor(() => null, {
      header: "Action Name",
      cell: ({ row }) => {
        return (
          <div
            style={{
              paddingLeft: `${row.depth * 2.5}rem`,
            }}
          >
            {row.original.name}
          </div>
        );
      },
      meta: {
        css: {
          width: "100%",
          minWidth: "20rem",
          padding: theme.spacing.spacing00,
        },
      },
    }),
    entityRow.accessor(() => null, {
      header: "Successful",
      cell: ({ row }) => {
        const counts = getCountsFromResourceStates(row, getCountsForAction);

        return (
          <Text color="success" mb={0}>
            {!counts.hasData || counts.succeeded === 0 ? "-" : counts.succeeded}
          </Text>
        );
      },
      meta: {
        css: { minWidth: "8.75rem", padding: theme.spacing.spacing00 },
      },
    }),
    entityRow.accessor(() => null, {
      header: "Unsuccessful",
      cell: ({ row }) => {
        const counts = getCountsFromResourceStates(row, getCountsForAction);

        if (counts.hasData && counts.notCompleted > 0) {
          return (
            <Text color="danger" mb={0}>
              {"-"}
            </Text>
          );
        }
        return (
          <Text color="danger" mb={0}>
            {!counts.hasData || counts.failed === 0 ? "-" : counts.failed}
          </Text>
        );
      },
      meta: {
        css: { minWidth: "8.75rem", padding: theme.spacing.spacing03 },
      },
    }),
  ];

  const descriptionColumn = entityRow.accessor(() => null, {
    header: "Details",
    cell: ({ row }) => {
      if (row.getCanExpand()) {
        return "-";
      }

      const description = getActionDescription(row.original);

      if (!description) {
        return "-";
      }

      return (
        <FlexContainer
          alignItems="center"
          gap={theme.spacing.spacing02}
          css={{ minWidth: 0 }}
        >
          {description.icon ? (
            <StyledSVGIcon
              name={description.icon}
              color={description.iconColor}
            />
          ) : null}
          <div>{description.text}</div>
        </FlexContainer>
      );
    },
    meta: {
      css: { minWidth: "18rem", padding: theme.spacing.spacing00 },
    },
  });

  const numberOfResourcesColumn = entityRow.accessor(() => null, {
    header: "Number of resources",
    cell: ({ row }) => {
      const counts = getCountsFromResourceStates(row, getCountsForAction);
      return (
        <div>{!counts.hasData || counts.total === 0 ? "-" : counts.total}</div>
      );
    },
    meta: {
      css: { minWidth: "8.75rem", padding: theme.spacing.spacing00 },
    },
  });

  const [expandColumn, statusColumn, nameColumn, ...statsColumns] = baseColumns;

  return [
    expandColumn,
    statusColumn,
    nameColumn,
    ...(isEventFinished ? [descriptionColumn] : []),
    numberOfResourcesColumn,
    ...statsColumns,
  ] as never;
}
